import React, { useContext, useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  VideoIcon,
  CalendarPlus,
  Upload,
  Plus,
  ArrowRight,
  Pencil,
  Trash2,
  Kanban,
  Video,
  Check,
  ChevronRight,
  X,
  Globe,
  Apple,
  Smartphone,
  Laptop,
  Computer,
  MonitorSmartphone,
  Link as LinkIcon,
  MessageSquare,
  Sparkle,
  Binoculars,
  Lightbulb,
  TrendingUp,
  Cpu,
  Loader2,
  Package,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel'
import { Badge } from '@/components/ui/badge'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { format } from 'date-fns'
import { AuthContext } from '@/context/AuthContext'
import { useWorkspace } from '@/context/WorkspaceContext'
import { DashboardContext } from '@/context/DashboardContext'
import { RoadmapContext } from '@/context/RoadmapContext'
import { RemoteConfigContext } from '@/context/RemoteConfigContext'
import { GoogleIcon } from '@/components/icons/Google'
import { MicrosoftIcon } from '@/components/icons/Microsoft'
import { ZoomIcon } from '@/components/icons/Zoom'
import { SlackIcon } from '@/components/icons/Slack'
import { GongIcon } from '@/components/icons/Gong'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { VideoCameraIcon } from '@/components/icons/VideoCamera'
import { ProjectLayout } from '@/components/dashboard/dialog/projectLayout'
import Calendar from '@/assets/welcomedashboard/apps/calendar.png'
import Microsoft from '@/assets/welcomedashboard/apps/microsoft.png'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { ImportFiles } from '@/components/dashboard/dialog/importFiles'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from '@/components/ui/select'
import { Dashboard } from '@uppy/react'
import Uppy from '@uppy/core'
import RemoteSources from '@uppy/remote-sources'
import AwsS3 from '@uppy/aws-s3'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import { ProjectSmallIcon } from '@/components/icons/Project'
import FeedItemRenderer from '@/components/feed/FeedItemRenderer'
import type { FeedItem, MeetingAnalysisItem } from '@/types/feedItems'
import { Workspace as ApiWorkspace } from '@/api-client'
import { uploadProductLogo } from '@/utils/uploadProductLogo'
import FirebaseStorageUploadDocuments from '@/utils/FirebaseStorageUploadDocuments'
import { JoinLiveMeetingDialog } from './JoinLiveMeetingDialog'
import { FeedContext } from '@/context/FeedContext'
import { MeetingSourceIcon } from '@/components/dashboard/meetings/MeetingSourceIcon'
import { getMeetingProvider } from '@/utils'
import { ChooseProject } from '@/components/dashboard/meetings/chooseProject'

// Types
interface Meeting {
  id: number
  name?: string
  start?: string
  created_at: number
  client?: string
  recording_url?: string
  thumbnail?: string
  product?: string
  provider?: string
  stored_name?: string
  meeting_url?: string
}

interface Participant {
  role: string
  name: string
}

interface UserIntegrations {
  googleCalendar?: boolean
  microsoftCalendar?: boolean
  zoom?: boolean
}

interface User {
  name: string
  integrations?: UserIntegrations
}

interface WorkspaceIntegrations {
  slack?: {
    connected: boolean
  }
  gong?: {
    connected: boolean
  }
}

interface Workspace extends ApiWorkspace {
  integrations?: WorkspaceIntegrations
}

export function HomeNew() {
  const { user } = useContext(AuthContext) as { user: User }
  const { workspace } = useWorkspace() as { workspace: Workspace }
  const { products, setProducts, meetings, upcomingMeetings, projectsLoading } = useContext(DashboardContext)
  const { allFeedItems, filteredFeedItems, feedsLoading, selectedTab, setSelectedTab, getFeedItems, hasMoreItems } =
    useContext(FeedContext)
  const { products: roadmapProducts, selectedProduct, changeProduct } = useContext(RoadmapContext)
  const { config, isLoading: remoteConfigLoading } = useContext(RemoteConfigContext)
  const feedContainerRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef(false)
  const [isFetching, setIsFetching] = useState(false)
  const scrollPositionRef = useRef(0)
  const navigate = useNavigate()
  const [showJoinLiveMeetingDialog, setShowJoinLiveMeetingDialog] = useState(false)
  const [showProjectDialog, setShowProjectDialog] = useState(false)
  const customFetch = useCustomFetch()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [productName, setProductName] = useState('')
  const [platform, setPlatform] = useState('')
  const [status, setStatus] = useState('ideation')
  const [tagline, setTagline] = useState('')
  const [description, setDescription] = useState('')
  const [uniqueSellingProposition, setUniqueSellingProposition] = useState('')
  const [documents, setDocuments] = useState<Array<{ fileName: string; storedFileName: string }>>([])
  const [links, setLinks] = useState<string[]>([])
  const [newLink, setNewLink] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const remoteSources = []
  if (!remoteConfigLoading) {
    if (config.googleDrive) remoteSources.push('GoogleDrive')
    if (config.oneDrive) remoteSources.push('OneDrive')
    if (config.zoom) remoteSources.push('Zoom')
    if (config.dropbox) remoteSources.push('Dropbox')
    if (config.box) remoteSources.push('Box')
  }

  const [uppy] = useState(() => {
    const newUppy = new Uppy({
      autoProceed: false,
      debug: true,
      restrictions: {
        maxFileSize: 10 * 1024 * 1024,
        maxNumberOfFiles: 10,
        allowedFileTypes: [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ],
      },
    })
      .use(RemoteSources, {
        companionUrl: import.meta.env.VITE_COMPANION_URL,
        sources: remoteSources,
      })
      .use(FirebaseStorageUploadDocuments, {})

    newUppy.on('file-added', (file) => {
      if (file.source && ['GoogleDrive', 'Zoom', 'Dropbox', 'OneDrive'].includes(file.source)) {
        newUppy.use(AwsS3, {
          endpoint: import.meta.env.VITE_COMPANION_URL,
        })
      } else {
        if (newUppy.getPlugin(AwsS3.name)) {
          newUppy.removePlugin(AwsS3 as any)
        }
      }
    })

    return newUppy
  })

  useEffect(() => {
    uppy.on('complete', async (result) => {
      try {
        if (result.successful.length) {
          const files = []
          for (const file of result.successful) {
            if (file.isRemote) {
              const fileLocation = file?.response?.body?.Key as string
              const storedFileName = fileLocation.substring(6)
              files.push({ size: file.size, storedFileName, type: file.type, fileName: file.name })
            } else {
              files.push({
                size: file.size,
                storedFileName: file.uploadURL,
                type: file.type,
                fileName: file.name,
              })
            }
          }
          if (!files.length) return

          setDocuments((prevDocuments) => [...prevDocuments, ...files])

          toast.success('Files uploaded!', {
            duration: 3000,
          })
        }
      } catch (error) {
        console.error('Error uploading files', error)
        toast.error('Whoops! Something went wrong. Please try again.')
      }
    })
  }, [uppy])

  const handleRemoveDocument = (storedFileName: string) => {
    setDocuments(documents.filter((doc) => doc.storedFileName !== storedFileName))
  }

  const handleAddLink = () => {
    if (newLink.trim() !== '') {
      setLinks([...links, newLink])
      setNewLink('')
    }
  }

  const handleRemoveLink = (linkToDelete: string) => {
    setLinks(links.filter((link) => link !== linkToDelete))
  }

  const filterTabs = [
    { value: 'all', label: 'All', icon: <Globe size={14} color="#FF4A8E" />, iconColor: 'text-[#FF4A8E]' },
    {
      value: 'meeting',
      label: 'Meetings',
      types: ['meeting'],
      icon: <VideoIcon size={14} />,
      iconColor: 'text-pink-700',
    },
    // { value: 'chat', label: 'Chats', types: ['chat'], icon: <MessageSquare size={14} />, iconColor: 'text-purple-700' },
    // {
    //   value: 'opportunity',
    //   label: 'Opportunities',
    //   types: ['opportunity'],
    //   icon: <Sparkle size={14} />,
    //   iconColor: 'text-purple-700',
    // },
    {
      value: 'competitor',
      label: 'Competitors',
      types: ['competitor'],
      icon: <Binoculars size={14} />,
      iconColor: 'text-blue-700',
    },
    {
      value: 'insight',
      label: 'Insights',
      types: ['insight'],
      icon: <Lightbulb size={14} />,
      iconColor: 'text-yellow-700',
    },
    // {
    //   value: 'market',
    //   label: 'Market',
    //   types: ['market'],
    //   icon: <TrendingUp size={14} />,
    //   iconColor: 'text-green-700',
    // },
    // { value: 'tech', label: 'Tech', types: ['tech'], icon: <Cpu size={14} />, iconColor: 'text-purple-700' },
  ]

  const getMoreFeedItems = async () => {
    console.log('getMoreFeedItems')
    if (isFetchingRef.current) return // Prevent multiple calls
    isFetchingRef.current = true
    setIsFetching(true)

    // Store current scroll position before fetching
    if (feedContainerRef.current) {
      scrollPositionRef.current = feedContainerRef.current.scrollTop
    }

    try {
      // Correctly call getFeedItems for infinite scroll
      // isNewProduct = false, isInfiniteScroll = true
      await getFeedItems(false, true)
    } finally {
      isFetchingRef.current = false
      setIsFetching(false)
    }
  }

  const handleScroll = useCallback(() => {
    if (!feedContainerRef.current || isFetchingRef.current) return

    if (!hasMoreItems) return

    const container = feedContainerRef.current
    const scrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight

    // Update scroll position ref
    scrollPositionRef.current = scrollTop

    // If we're near the bottom (within 500px), load more items
    if (scrollTop + clientHeight >= scrollHeight - 500) {
      getMoreFeedItems()
    }
  }, [getMoreFeedItems, hasMoreItems])

  useEffect(() => {
    const container = feedContainerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)
    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  // Effect to preserve scroll position after re-renders during infinite scroll
  useEffect(() => {
    if (feedContainerRef.current && scrollPositionRef.current > 0 && !feedsLoading) {
      // Only restore scroll position if we're not at the top (which would indicate a filter change)
      const container = feedContainerRef.current
      if (container.scrollTop === 0 && scrollPositionRef.current > 100) {
        container.scrollTop = scrollPositionRef.current
      }
    }
  }, [filteredFeedItems, feedsLoading])

  const handleFilterChange = (value: string) => {
    setSelectedTab(value)

    // Reset scroll position reference when filter changes
    scrollPositionRef.current = 0

    // Scroll back to top when filter changes
    if (feedContainerRef.current) {
      feedContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }

    // Remove manual filtering logic - let the context handle this automatically
    // The FeedContext useEffect will handle filtering based on selectedTab changes
  }

  // Get connected integrations
  const connectedIntegrations = [
    ...(user?.integrations?.googleCalendar ? [{ name: 'Google Calendar', icon: <GoogleIcon /> }] : []),
    ...(user?.integrations?.microsoftCalendar ? [{ name: 'Microsoft Calendar', icon: <MicrosoftIcon /> }] : []),
    ...(user?.integrations?.zoom ? [{ name: 'Zoom', icon: <ZoomIcon /> }] : []),
    ...(workspace?.integrations?.slack?.connected ? [{ name: 'Slack', icon: <SlackIcon /> }] : []),
    ...(workspace?.integrations?.gong?.connected ? [{ name: 'Gong', icon: <GongIcon /> }] : []),
  ]

  const getDateDisplay = (meeting: Meeting) => {
    const today = new Date()
    const tomorrow = new Date()
    tomorrow.setDate(today.getDate() + 1)

    const meetingDate = meeting.start ? new Date(meeting.start) : new Date(meeting.created_at)
    const formattedTime = format(meetingDate, 'h:mma')

    if (meetingDate.toDateString() === today.toDateString()) {
      return `Today・${formattedTime}`
    } else if (meetingDate.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow・${formattedTime}`
    } else {
      return `${format(meetingDate, 'MMMM do')}・${formattedTime}`
    }
  }

  const generateCalendarLink = async () => {
    if (user.integrations?.googleCalendar) {
      const baseUrl = 'https://calendar.google.com/calendar/render?action=TEMPLATE'
      const title = encodeURIComponent('New User Interview')
      const guests = encodeURIComponent(`invite+${workspace.stem}@usermuse.ai`)
      const eventLink = `${baseUrl}&text=${title}&add=${guests}`
      window.open(eventLink, '_blank')
    } else if (user.integrations?.microsoftCalendar) {
      const { isBusinessAccount, error } = await customFetch('/auth/isBusinessAccount')
      if (error) return toast.error('Failed to generate calendar link')
      const baseUrl = `https://outlook.${isBusinessAccount ? 'office' : 'live'}.com/calendar/0/deeplink/compose`
      const title = encodeURIComponent('New User Interview')
      const guests = encodeURIComponent(`invite+${workspace.stem}@usermuse.ai`)
      const eventLink = `${baseUrl}?subject=${title}&to=${guests}`
      window.open(eventLink, '_blank')
    } else {
      navigate('/dashboard/integrations')
    }
  }

  const handleImportFiles = async (files: File[]) => {
    if (!selectedProduct?.id) return

    try {
      const formData = new FormData()
      files.forEach((file) => {
        formData.append('files', file)
      })
      formData.append('projectId', selectedProduct.id)

      const response = await customFetch('/api/uploadRecording', {
        method: 'POST',
        body: formData,
      })

      if (response.ok) {
        toast.success('Files uploaded successfully')
        setShowProjectDialog(false)
      } else {
        throw new Error('Failed to upload files')
      }
    } catch (error) {
      console.error('Error uploading files:', error)
      toast.error('Failed to upload files')
    }
  }

  const resetForm = () => {
    setProductName('')
    setPlatform('')
    setStatus('ideation')
    setTagline('')
    setDescription('')
    setUniqueSellingProposition('')
    setSelectedImage(null)
    setDocuments([])
    setLinks([])
  }

  const handleCreateProduct = async () => {
    if (!productName.trim()) return toast.error('Product name is required')
    if (!tagline.trim()) return toast.error('Tagline is required')
    if (!platform.trim()) return toast.error('Platform is required')
    if (!status.trim()) return toast.error('Status is required')
    if (!description.trim()) return toast.error('Description is required')
    if (!uniqueSellingProposition.trim()) return toast.error('Unique Selling Proposition is required')

    const newProduct = {
      name: productName,
      tagline: tagline,
      description: description,
      uniqueSellingProposition,
      status: status === 'active' ? 'Live' : status.charAt(0).toUpperCase() + status.slice(1),
      platform:
        platform === 'web'
          ? 'Web'
          : platform === 'ios'
            ? 'iOS'
            : platform === 'android'
              ? 'Android'
              : platform === 'macos'
                ? 'Mac OS'
                : platform === 'windows'
                  ? 'Windows'
                  : platform === 'linux'
                    ? 'Linux'
                    : platform === 'multiplatform'
                      ? 'Multi-Platform'
                      : 'Web',
      productLogo: selectedImage,
      order: products.length + 1,
      links,
      documents,
    }

    try {
      const response = await customFetch('/createProduct', 'POST', { ...newProduct })
      setProducts((prevProducts) => [...prevProducts, { ...newProduct, id: response.id, projectsData: [] }])
      resetForm()
      setAddDialogOpen(false)
      toast.success('Product created successfully')
    } catch (error) {
      console.error('Error creating product:', error)
      toast.error('Failed to create product')
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      try {
        setIsUploading(true)
        setUploadProgress(0)
        const downloadURL = await uploadProductLogo(file, (progress) => {
          setUploadProgress(progress)
        })
        setSelectedImage(downloadURL)
        toast.success('Logo uploaded successfully')
      } catch (error) {
        console.error('Error uploading logo:', error)
        toast.error('Failed to upload logo')
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
      }
    }
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
  }

  return (
    <div className="h-full bg-canvas">
      <div className="flex gap-6 h-full">
        {/* Main column - Feed */}
        <div className="flex-1 h-full overflow-y-auto hideScrollbar" ref={feedContainerRef}>
          <div className="sticky top-0 h-[var(--topbar-height)] bg-canvas z-10">{/* Feed Spacer */}</div>

          {/* Feed Tab bar (filters) */}
          <div className="sticky top-[calc(var(--topbar-height)-1px)] z-10 mb-4 container max-w-4xl mx-auto">
            <div className=" bg-white rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center gap-4 px-4 py-2.5">
                <Select value={selectedProduct?.id} onValueChange={changeProduct}>
                  <SelectTrigger className="w-fit min-w-[180px] bg-white text-base rounded-full border border-gray-200 hover:bg-gray-50 h-8 px-3">
                    <Package className="w-4 h-4 mr-2 text-[#FF4A8E]" />
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg border border-gray-200">
                    <SelectGroup>
                      {roadmapProducts.map((product) => (
                        <SelectItem key={product.id} value={product.id} className="focus:bg-gray-50">
                          {product.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <div className="flex-1 overflow-hidden">
                  <Tabs value={selectedTab} onValueChange={handleFilterChange} className="w-full">
                    <ScrollArea type="always" className="w-full scrollbar-hide">
                      <TabsList className="w-fit bg-white dark-mode:bg-canvas-dark p-0.5 rounded-full space-x-2">
                        {filterTabs.map((tab) => (
                          <TabsTrigger
                            key={tab.value}
                            value={tab.value}
                            className="
                                  flex-shrink-0 
                                  rounded-full 
                                  border border-gray-200 
                                  dark-mode:border-gray-700
                                  bg-gray-50
                                  data-[state=active]:bg-white 
                                  data-[state=active]:shadow-sm 
                                  data-[state=active]:border-white
                                  dark-mode:data-[state=active]:bg-gray-700
                                  dark-mode:data-[state=active]:border-gray-700
                                  dark-mode:data-[state=active]:text-white
                                  data-[state=active]:ring-1
                                  data-[state=active]:ring-gray-200
                                  data-[state=active]:ring-offset-1
                                  dark-mode:data-[state=active]:ring-gray-600
                                  transition-all
                                  duration-200
                                  h-8
                                  px-4 
                                  text-sm
                                  hover:bg-gray-100
                                  dark-mode:hover:bg-gray-700
                                  group
                                  relative
                                "
                          >
                            <span className={`${selectedTab === tab.value ? 'flex items-center gap-1' : ''}`}>
                              {selectedTab === tab.value && <span className={tab.iconColor}>{tab.icon}</span>}
                              {tab.label}
                            </span>
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      <ScrollBar orientation="horizontal" className="hidden" />
                    </ScrollArea>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-4 mt-4 container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            {feedsLoading ? (
              <div className="text-center py-6 text-gray-500">
                <p className="text-sm">Loading news...</p>
              </div>
            ) : filteredFeedItems.length > 0 ? (
              <>
                {filteredFeedItems.map((item, index) => (
                  <FeedItemRenderer key={`${index}-${item.type}`} item={item} />
                ))}

                {/* Loading indicator */}
                {isFetching && (
                  <div className="flex justify-center items-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading more...</span>
                  </div>
                )}

                {/* End of feed indicator */}
                {allFeedItems[selectedProduct.id] &&
                  allFeedItems[selectedProduct.id].filter((item) => selectedTab === 'all' || item.type === selectedTab)
                    .length > 0 &&
                  !hasMoreItems &&
                  filteredFeedItems.length ===
                    allFeedItems[selectedProduct.id].filter(
                      (item) => selectedTab === 'all' || item.type === selectedTab
                    ).length && (
                    <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 mt-2">
                      <p className="text-sm">You've reached the end of the feed</p>
                    </div>
                  )}
              </>
            ) : !selectedProduct?.id ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                <Package className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-lg font-medium">Select a product to view feed</p>
                <p className="text-sm">Choose a product from the dropdown above to see its feed items</p>
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                <Globe className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-lg font-medium">No items match this filter</p>
                <p className="text-sm">Try selecting a different category</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar column */}
        <div className="w-[390px] flex-shrink-0 space-y-6 h-[calc(100vh-var(--topbar-height)-2px)] overflow-y-auto scrollbar-hide mr-8 mt-[calc(var(--topbar-height)-1px)] pb-7">
          {/* Add Meetings Card */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-medium">Add Meetings</CardTitle>
              <VideoIcon size={20} className="text-gray-400 dark:text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Schedule a new meeting with users or team members
              </div>
              <Button
                className="w-full bg-[#FF4A8E] hover:bg-[#E43D7A] mb-4"
                onClick={() => setShowJoinLiveMeetingDialog(true)}
              >
                <VideoIcon size={16} className="mr-2" />
                Join Live Meeting
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="secondary"
                  className="w-full hover:bg-gray-200 dark:hover:bg-gray-700"
                  onClick={generateCalendarLink}
                >
                  {!user?.integrations?.googleCalendar && !user?.integrations?.microsoftCalendar ? (
                    <>
                      <CalendarPlus size={16} className="mr-2" />
                      Connect Calendar
                    </>
                  ) : (
                    <>
                      <img
                        src={user.integrations?.googleCalendar ? Calendar : Microsoft}
                        className="w-4 h-4 mr-2"
                        alt="Calendar"
                      />
                      Schedule
                    </>
                  )}
                </Button>
                <Button
                  variant="secondary"
                  className="w-full hover:bg-gray-200 dark:hover:bg-gray-700"
                  onClick={() => setShowProjectDialog(true)}
                >
                  <Upload size={16} className="mr-2" />
                  Upload
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Integrations Card */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-medium">Active Integrations</CardTitle>
              <Link
                to="/dashboard/integrations"
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 flex items-center"
              >
                View All
                <ChevronRight size={16} />
              </Link>
            </CardHeader>
            <CardContent>
              {connectedIntegrations.length > 0 ? (
                <Carousel
                  opts={{
                    align: 'start',
                    loop: true,
                  }}
                  className="w-full"
                >
                  <CarouselContent className="py-1 px-1">
                    {connectedIntegrations.map((integration, index) => (
                      <CarouselItem key={index} className="basis-1/3 pl-1">
                        <div className="flex flex-col items-center">
                          <div className="relative">
                            <div className="w-16 h-16 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 flex items-center justify-center overflow-hidden relative shadow-sm">
                              {integration.icon}
                            </div>
                            <Badge className="absolute -bottom-1 -right-1 bg-[#F2FCE2] text-green-700 border border-green-200 rounded-full p-0.5">
                              <Check size={12} />
                            </Badge>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-300 mt-2 text-center">
                            {integration.name}
                          </span>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <p className="text-sm">No active integrations</p>
                  <Link to="/dashboard/integrations" className="text-[#FF4A8E] text-sm hover:underline">
                    Connect integrations
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Meetings Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium">Upcoming Meetings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {upcomingMeetings.slice(0, 10).map((meeting) => (
                  <div
                    key={meeting.id}
                    className="block transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 -mx-2"
                  >
                    <div className="flex items-start justify-between">
                      <a
                        href={meeting.meeting_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start space-x-3 flex-1"
                      >
                        <div className="flex-shrink-0">
                          <MeetingSourceIcon
                            source={
                              meeting.source
                                ? meeting.source.toLowerCase()
                                : getMeetingProvider(meeting?.meeting_url)?.toLowerCase()
                            }
                            width="16"
                            height="16"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {meeting.name || 'Untitled Meeting'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {getDateDisplay(meeting as Meeting)}
                          </p>
                        </div>
                      </a>
                      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <ChooseProject meeting={meeting} />
                      </div>
                    </div>
                  </div>
                ))}
                {meetings.filter((meeting) => {
                  const meetingDate = meeting.start ? new Date(meeting.start) : new Date(meeting.created_at)
                  return meetingDate > new Date()
                }).length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    <CalendarPlus className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
                    <p className="text-sm">No upcoming meetings scheduled</p>
                    <p className="text-xs mt-1">Schedule a meeting to see it here</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Products Overview Card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg font-medium">Products</CardTitle>
                <Button
                  className="bg-[#FF4A8E] text-white hover:bg-[#FF4A8E]/90"
                  onClick={() => setAddDialogOpen(true)}
                >
                  <Plus size={16} />
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {projectsLoading ? (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <p className="text-sm">Loading products...</p>
                </div>
              ) : products.length > 0 ? (
                <>
                  <Accordion type="single" collapsible className="w-full mb-4">
                    {products.map((product) => (
                      <AccordionItem key={product.id} value={`item-${product.id}`}>
                        <div className="group relative">
                          <AccordionTrigger className="py-2 hover:no-underline">
                            <div className="flex justify-between items-center w-full pr-2">
                              <div className="flex items-center gap-2 font-medium text-left">
                                {product.platform?.includes('Web') ? (
                                  <Globe
                                    size={16}
                                    className={`${
                                      product.status === 'Ideation'
                                        ? 'text-[#DCAE1D]'
                                        : product.status === 'Planning'
                                          ? 'text-[#2C8FB1]'
                                          : product.status === 'Alpha'
                                            ? 'text-[#CF3F75]'
                                            : product.status === 'Beta'
                                              ? 'text-[#964EB2]'
                                              : product.status === 'Live'
                                                ? 'text-[#B2C920]'
                                                : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  />
                                ) : product.platform?.includes('iOS') ? (
                                  <Apple
                                    size={16}
                                    className={`${
                                      product.status === 'Ideation'
                                        ? 'text-[#DCAE1D]'
                                        : product.status === 'Planning'
                                          ? 'text-[#2C8FB1]'
                                          : product.status === 'Alpha'
                                            ? 'text-[#CF3F75]'
                                            : product.status === 'Beta'
                                              ? 'text-[#964EB2]'
                                              : product.status === 'Live'
                                                ? 'text-[#B2C920]'
                                                : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  />
                                ) : product.platform?.includes('Android') ? (
                                  <Smartphone
                                    size={16}
                                    className={`${
                                      product.status === 'Ideation'
                                        ? 'text-[#DCAE1D]'
                                        : product.status === 'Planning'
                                          ? 'text-[#2C8FB1]'
                                          : product.status === 'Alpha'
                                            ? 'text-[#CF3F75]'
                                            : product.status === 'Beta'
                                              ? 'text-[#964EB2]'
                                              : product.status === 'Live'
                                                ? 'text-[#B2C920]'
                                                : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  />
                                ) : product.platform?.includes('Mac OS') ? (
                                  <Laptop
                                    size={16}
                                    className={`${
                                      product.status === 'Ideation'
                                        ? 'text-[#DCAE1D]'
                                        : product.status === 'Planning'
                                          ? 'text-[#2C8FB1]'
                                          : product.status === 'Alpha'
                                            ? 'text-[#CF3F75]'
                                            : product.status === 'Beta'
                                              ? 'text-[#964EB2]'
                                              : product.status === 'Live'
                                                ? 'text-[#B2C920]'
                                                : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  />
                                ) : product.platform?.includes('Windows') ? (
                                  <Computer
                                    size={16}
                                    className={`${
                                      product.status === 'Ideation'
                                        ? 'text-[#DCAE1D]'
                                        : product.status === 'Planning'
                                          ? 'text-[#2C8FB1]'
                                          : product.status === 'Alpha'
                                            ? 'text-[#CF3F75]'
                                            : product.status === 'Beta'
                                              ? 'text-[#964EB2]'
                                              : product.status === 'Live'
                                                ? 'text-[#B2C920]'
                                                : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  />
                                ) : product.platform?.includes('Linux') ? (
                                  <Computer
                                    size={16}
                                    className={`${
                                      product.status === 'Ideation'
                                        ? 'text-[#DCAE1D]'
                                        : product.status === 'Planning'
                                          ? 'text-[#2C8FB1]'
                                          : product.status === 'Alpha'
                                            ? 'text-[#CF3F75]'
                                            : product.status === 'Beta'
                                              ? 'text-[#964EB2]'
                                              : product.status === 'Live'
                                                ? 'text-[#B2C920]'
                                                : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  />
                                ) : product.platform?.includes('Multi-Platform') ? (
                                  <MonitorSmartphone
                                    size={16}
                                    className={`${
                                      product.status === 'Ideation'
                                        ? 'text-[#DCAE1D]'
                                        : product.status === 'Planning'
                                          ? 'text-[#2C8FB1]'
                                          : product.status === 'Alpha'
                                            ? 'text-[#CF3F75]'
                                            : product.status === 'Beta'
                                              ? 'text-[#964EB2]'
                                              : product.status === 'Live'
                                                ? 'text-[#B2C920]'
                                                : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  />
                                ) : (
                                  <Globe
                                    size={16}
                                    className={`${
                                      product.status === 'Ideation'
                                        ? 'text-[#DCAE1D]'
                                        : product.status === 'Planning'
                                          ? 'text-[#2C8FB1]'
                                          : product.status === 'Alpha'
                                            ? 'text-[#CF3F75]'
                                            : product.status === 'Beta'
                                              ? 'text-[#964EB2]'
                                              : product.status === 'Live'
                                                ? 'text-[#B2C920]'
                                                : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                  />
                                )}
                                {product.name}
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      // Add your edit handler here
                                    }}
                                    className="h-7 w-7 p-0 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    <Pencil size={14} className="text-gray-500 dark:text-gray-400" />
                                  </div>
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      // Add your add handler here
                                    }}
                                    className="h-7 w-7 p-0 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    <Plus size={14} className="text-gray-500 dark:text-gray-400" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </AccordionTrigger>
                        </div>
                        <AccordionContent>
                          <div className="pl-2 space-y-2 pt-1 pb-2">
                            {product.projectsData && product.projectsData.length > 0 ? (
                              product.projectsData.map((project, index) => (
                                <div
                                  key={index}
                                  className="flex items-center text-sm border-b border-gray-100 dark:border-gray-700 pb-1 last:border-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded p-1 transition-colors"
                                  onClick={() => navigate(`/project/${project.id}`)}
                                >
                                  <div className="mr-1.5 shrink-0">
                                    <ProjectSmallIcon disabled={false} />
                                  </div>
                                  <div className="flex justify-between items-center w-full">
                                    <span className="dark:text-gray-200">{project.name}</span>
                                    <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                                      <Video size={12} className="mr-1 text-blue-500" />
                                      <span>{project.meetings_count || 0} meetings</span>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                <p className="text-sm">No projects yet</p>
                                <Button
                                  variant="ghost"
                                  className="text-[#FF4A8E] text-xs hover:underline mt-1 border border-[#FF4A8E] h-7 px-3"
                                >
                                  Create one
                                </Button>
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  <Link to="/dashboard/products">
                    <Button
                      variant="outline"
                      className="w-full flex justify-between items-center dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-white"
                    >
                      View All Products
                      <ArrowRight size={16} />
                    </Button>
                  </Link>
                </>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <p className="text-sm">No products found</p>
                  <Button variant="ghost" className="text-[#FF4A8E] text-sm hover:underline mt-2">
                    Add your first product
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      {showJoinLiveMeetingDialog && <JoinLiveMeetingDialog handleClose={() => setShowJoinLiveMeetingDialog(false)} />}
      {showProjectDialog &&
        (createPortal(
          <ProjectLayout importFile={true} handleClose={() => setShowProjectDialog(false)} />,
          document.body
        ) as React.ReactPortal)}

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-[900px] rounded-2xl h-[80vh] p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="text-xl font-semibold">Add New Product</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="grid gap-6">
              {/* Logo & Product details */}
              <div className="grid grid-cols-[120px_1fr] gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Logo</label>
                  <div className="h-[120px] w-[120px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative">
                    {selectedImage ? (
                      <>
                        <img src={selectedImage} alt="Product Logo" className="w-full h-full object-cover rounded-lg" />
                        <button
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm hover:bg-gray-100"
                        >
                          <X size={16} className="text-gray-500" />
                        </button>
                      </>
                    ) : (
                      <div className="text-center">
                        <Upload size={24} className="mx-auto text-gray-400" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <p className="text-xs text-gray-500 mt-1">Click to upload</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium">
                      Product Name*
                    </label>
                    <input
                      id="name"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter product name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="tagline" className="text-sm font-medium">
                      Tagline*
                    </label>
                    <input
                      id="tagline"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Enter a short tagline"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="platform" className="text-sm font-medium">
                    Platform*
                  </label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger id="platform">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="web">
                        <div className="flex items-center gap-2">
                          <Globe size={16} className="text-gray-700" />
                          <span>Web</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="ios">
                        <div className="flex items-center gap-2">
                          <Apple size={16} className="text-gray-700" />
                          <span>iOS</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="android">
                        <div className="flex items-center gap-2">
                          <Smartphone size={16} className="text-gray-700" />
                          <span>Android</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="macos">
                        <div className="flex items-center gap-2">
                          <Laptop size={16} className="text-gray-700" />
                          <span>Mac OS</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="windows">
                        <div className="flex items-center gap-2">
                          <Computer size={16} className="text-gray-700" />
                          <span>Windows</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="linux">
                        <div className="flex items-center gap-2">
                          <Computer size={16} className="text-gray-700" />
                          <span>Linux</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="multiplatform">
                        <div className="flex items-center gap-2">
                          <MonitorSmartphone size={16} className="text-gray-700" />
                          <span>Multi-Platform</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="status" className="text-sm font-medium">
                    Status*
                  </label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ideation">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#DCAE1D]" />
                          <span>Ideation</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="planning">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#2C8FB1]" />
                          <span>Planning</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="alpha">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#CF3F75]" />
                          <span>Alpha</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="beta">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#964EB2]" />
                          <span>Beta</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="active">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#B2C920]" />
                          <span>Live</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium">
                  Description*
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Describe your product in detail"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="usp" className="text-sm font-medium">
                  Unique Selling Proposition*
                </label>
                <textarea
                  id="usp"
                  value={uniqueSellingProposition}
                  onChange={(e) => setUniqueSellingProposition(e.target.value)}
                  className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="What makes your product special? What's your competitive advantage?"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Documents</label>
                  <div className="border rounded-lg overflow-hidden h-[180px]">
                    <Dashboard
                      target="#uppy-dashboard"
                      uppy={uppy}
                      plugins={remoteSources}
                      showProgressDetails={true}
                      note="Acceptable file types: PDF, Word, PPT, Excel (Max 10MB)"
                      width="100%"
                      height="180px"
                    />
                  </div>
                  {documents.length > 0 && (
                    <div className="mt-4 max-h-[80px] overflow-y-auto">
                      {documents.map((doc) => (
                        <div
                          key={doc.storedFileName}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-md mb-2"
                        >
                          <span className="text-sm text-gray-600">{doc.fileName}</span>
                          <button
                            onClick={() => handleRemoveDocument(doc.storedFileName)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Links</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      value={newLink}
                      onChange={(e) => setNewLink(e.target.value)}
                      className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="https://example.com"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddLink()
                        }
                      }}
                    />
                    <Button variant="outline" size="icon" onClick={handleAddLink}>
                      <Plus size={16} />
                    </Button>
                  </div>

                  {links.length > 0 && (
                    <div className="h-[120px] overflow-y-auto space-y-2">
                      {links.map((link, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                          <LinkIcon size={14} className="text-gray-500" />
                          <span className="text-sm text-gray-600 flex-1">{link}</span>
                          <button onClick={() => handleRemoveLink(link)} className="text-gray-400 hover:text-gray-600">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t mt-auto">
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-[#FF4A8E] hover:bg-[#FF4A8E]/90" onClick={handleCreateProduct}>
              Create Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
